import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, CheckCircleIcon, PauseCircleIcon, XCircleIcon } from "@phosphor-icons/react";

import FormattedNumberInput from "@/components/FormattedNumberInput";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

export default function Quality() {
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = {
    order_id: "", item_id: "", machine_id: "",
    inspection_date: new Date().toISOString().slice(0, 10),
    width_mm: 25, stretch_pct: 130, recovery_pct: 90,
    shade: "OK", appearance: "OK", defects: "", lot_no: "",
    inspector: "", status: "Pass",
  };
  const [form, setForm] = useState(empty);
  // Feb 2026 · one-tap decision dialog holds the target row + chosen action
  // + free-text reason. Rejects REQUIRE a reason (backend enforces it too).
  const [decision, setDecision] = useState(null); // { row, action, reason }

  const load = async () => {
    const [q, o, it, m] = await Promise.all([
      api.get("/quality/"), api.get("/sales-orders/"), api.get("/items/"), api.get("/machines/"),
    ]);
    setRows(q.data); setOrders(dedupById(o.data)); setItems(it.data); setMachines(m.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/quality/", form);
      toast.success("Inspection recorded");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/quality/${id}`);
    toast.success("Deleted"); load();
  };

  const openDecide = (row, action) => setDecision({ row, action, reason: "" });
  const submitDecision = async () => {
    if (!decision) return;
    if (decision.action === "Reject" && !decision.reason.trim()) {
      toast.error("A reason is required to reject a lot");
      return;
    }
    try {
      await api.post(`/quality/${decision.row.id}/decide`, {
        decision: decision.action,
        reason: decision.reason.trim(),
      });
      toast.success(`Lot ${decision.row.lot_no || decision.row.id.slice(0, 6)} → ${decision.action}`);
      setDecision(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    }
  };

  const name = (arr, id, k = "code") => arr.find(x => x.id === id)?.[k] || "—";
  const stats = {
    Pass: rows.filter(r => r.status === "Pass").length,
    Hold: rows.filter(r => r.status === "Hold").length,
    Reject: rows.filter(r => r.status === "Reject").length,
  };

  const DecideBtn = ({ row, action, testid, icon: Icon, cls }) => (
    <button
      onClick={(e) => { e.stopPropagation(); openDecide(row, action); }}
      data-testid={`${testid}-${row.id}`}
      title={`Mark as ${action}`}
      disabled={row.status === action}
      className={`p-1.5 rounded-sm border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${cls}`}
    >
      <Icon size={14} weight="bold" />
    </button>
  );

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Compliance</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Quality Inspections</h1>
        </div>
        <button data-testid="new-quality-btn" onClick={() => setOpen(true)} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" />New Inspection
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="tactical-card p-4">
          <div className="overline text-emerald-400 mb-1">Pass</div>
          <div className="font-mono text-3xl text-white">{stats.Pass}</div>
        </div>
        <div className="tactical-card p-4">
          <div className="overline text-amber-400 mb-1">Hold</div>
          <div className="font-mono text-3xl text-white">{stats.Hold}</div>
        </div>
        <div className="tactical-card p-4">
          <div className="overline text-red-400 mb-1">Reject</div>
          <div className="font-mono text-3xl text-white">{stats.Reject}</div>
        </div>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-5 py-3 font-medium">Date</th>
              <th className="text-left px-5 py-3 font-medium">Order</th>
              <th className="text-left px-5 py-3 font-medium">Item</th>
              <th className="text-left px-5 py-3 font-medium">Machine</th>
              <th className="text-right px-5 py-3 font-medium">Width mm</th>
              <th className="text-right px-5 py-3 font-medium">Stretch %</th>
              <th className="text-right px-5 py-3 font-medium">Recovery %</th>
              <th className="text-left px-5 py-3 font-medium">Lot</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-center px-5 py-3 font-medium">Decision</th>
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`qc-${r.id}`}>
                <td className="px-5 py-3 font-mono text-white">{r.inspection_date}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{name(orders, r.order_id, "order_no")}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{name(items, r.item_id)}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{name(machines, r.machine_id)}</td>
                <td className="px-5 py-3 text-right font-mono text-white">{r.width_mm}</td>
                <td className="px-5 py-3 text-right font-mono text-white">{r.stretch_pct}</td>
                <td className="px-5 py-3 text-right font-mono text-white">{r.recovery_pct}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{r.lot_no}</td>
                <td className="px-5 py-3">
                  <StatusBadge status={r.status} />
                  {r.last_decision_reason && (
                    <div className="text-[10px] text-zinc-500 mt-1 truncate max-w-[160px]" title={r.last_decision_reason}>
                      · {r.last_decision_reason}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <DecideBtn row={r} action="Pass"   testid="qc-decide-pass"   icon={CheckCircleIcon} cls="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" />
                    <DecideBtn row={r} action="Hold"   testid="qc-decide-hold"   icon={PauseCircleIcon} cls="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" />
                    <DecideBtn row={r} action="Reject" testid="qc-decide-reject" icon={XCircleIcon}     cls="border-red-500/30 text-red-400 hover:bg-red-500/10" />
                  </div>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => remove(r.id)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><TrashIcon size={14} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="text-center py-16 text-zinc-500">No inspections yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-2xl" description="Quality inspection workflow — capture defects, quantities and Passed/Rejected verdict." onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display">New Inspection</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Date"><input type="date" value={form.inspection_date} onChange={e => setForm({...form, inspection_date: e.target.value})} className={inp} /></F>
            <F label="Lot No"><input data-testid="qc-lot" value={form.lot_no} onChange={e => setForm({...form, lot_no: e.target.value})} className={inp} /></F>
            <F label="Order">
              <Select value={form.order_id} onValueChange={v => setForm({...form, order_id: v})}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {orders.map(o => <SelectItem key={o.id} value={o.id}>{o.order_no}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Item">
              <Select value={form.item_id} onValueChange={v => setForm({...form, item_id: v})}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {items.map(i => <SelectItem key={i.id} value={i.id}>{i.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Machine">
              <Select value={form.machine_id} onValueChange={v => setForm({...form, machine_id: v})}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Inspector"><input value={form.inspector} onChange={e => setForm({...form, inspector: e.target.value})} className={inp} /></F>
            <F label="Width (mm)"><FormattedNumberInput step="0.1" value={form.width_mm} onChange={e => setForm({...form, width_mm: parseFloat(e.target.value) || 0})} className={inp} /></F>
            <F label="Stretch %"><FormattedNumberInput step="0.1" value={form.stretch_pct} onChange={e => setForm({...form, stretch_pct: parseFloat(e.target.value) || 0})} className={inp} /></F>
            <F label="Recovery %"><FormattedNumberInput step="0.1" value={form.recovery_pct} onChange={e => setForm({...form, recovery_pct: parseFloat(e.target.value) || 0})} className={inp} /></F>
            <F label="Shade"><input value={form.shade} onChange={e => setForm({...form, shade: e.target.value})} className={inp} /></F>
            <F label="Appearance"><input value={form.appearance} onChange={e => setForm({...form, appearance: e.target.value})} className={inp} /></F>
            <F label="Defects" className="col-span-2"><input value={form.defects} onChange={e => setForm({...form, defects: e.target.value})} className={inp} /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger data-testid="qc-status" className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {["Pass","Hold","Reject"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-qc" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-tap decision dialog */}
      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-md" data-testid="qc-decide-dialog" description="Quality inspection workflow — capture defects, quantities and Passed/Rejected verdict." onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {decision?.action === "Pass" && <CheckCircleIcon size={20} weight="fill" className="text-emerald-400" />}
              {decision?.action === "Hold" && <PauseCircleIcon size={20} weight="fill" className="text-amber-400" />}
              {decision?.action === "Reject" && <XCircleIcon size={20} weight="fill" className="text-red-400" />}
              Mark Lot as {decision?.action}
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="space-y-3">
              <div className="p-3 bg-[var(--bg-2)] rounded-sm text-sm">
                <div className="overline mb-1">Lot</div>
                <div className="font-mono text-white">{decision.row.lot_no || <span className="text-zinc-500">unlabelled</span>}</div>
                <div className="text-xs text-zinc-500 mt-1">Inspection · {decision.row.inspection_date}</div>
              </div>
              <F label={`Reason ${decision.action === "Reject" ? "*" : "(optional)"}`}>
                <textarea
                  data-testid="qc-decide-reason"
                  value={decision.reason}
                  onChange={e => setDecision({ ...decision, reason: e.target.value })}
                  placeholder={decision.action === "Reject" ? "Why is this lot being rejected?" : "Optional note"}
                  rows={3}
                  className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]"
                />
              </F>
              {(decision.row.last_decision_reason) && (
                <div className="text-[11px] text-zinc-500">
                  Previous: <span className="text-zinc-400">"{decision.row.last_decision_reason}"</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDecision(null)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="qc-decide-submit" onClick={submitDecision} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Confirm {decision?.action}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}
